/**
 * The console's view of the API.
 *
 * These types are hand-written against `packages/api/src/review.ts` rather than
 * generated, and that is a temporary state with a named exit: the architecture
 * calls for Zod schemas in `packages/contracts` to emit OpenAPI 3.1 and for CI
 * to generate a typed client from it, so the documentation cannot drift. Until
 * that lands, this file is the seam, and it is one file so the drift has one
 * place to be found.
 *
 * Two things about the transport:
 *
 * **No `Authorization` header anywhere.** The session is two `HttpOnly` cookies
 * (`po_machine`, `po_operator`) set at sign-in. Script cannot read them, which
 * is the point — and it is also what lets a bare `<video src>` and
 * `navigator.sendBeacon` authenticate, neither of which can set a header. Every
 * call here is `credentials: 'same-origin'` and carries nothing else.
 *
 * **Durations are decimal strings, not numbers.** The server sends
 * `measured_duration_seconds` as a string because it is a Postgres `numeric`,
 * and parsing it into a float on the way in would put a second rounding site in
 * the client. The client formats these for display and never does arithmetic
 * that reaches a payment: the server computes money.
 */

/** A Postgres `numeric` as it arrives. Display it; never total it. */
export type Decimal = string;

export interface BrowsedEpisode {
  episode_id: string;
  task_name: string | null;
  collector_ref: string | null;
  device_serial: string | null;
  resolution_state: 'resolved' | 'quarantined';
  first_seen_at: string;
  session_started_at: string;
}

export const browseEpisodes = (search: string) =>
  call<{ episodes: BrowsedEpisode[]; truncated: boolean; next: string | null }>(`/api/episodes${search ? `?${search}` : ''}`);

export type Verdict = 'good' | 'partial' | 'bad';
export type ReviewQueue = 'standard' | 'privacy' | 'second_review';
export type DemoReviewAction = 'move_standard' | 'accept' | 'deny' | 'flag';

export interface ReviewCatalogItem {
  episode_id: string;
  session_folder: string;
  task_name?: string | null;
  filename?: string | null;
  thumbnail_url?: string | null;
  collector_label: string | null;
  collector_ref: string | null;
  recorded_at: string | null;
  uploaded_at: string | null;
  duration_seconds: number | string | null;
  source: 'ego' | 'phone';
  state: string;
  queue: ReviewQueue;
  claimable: boolean;
  preview_url: string | null;
  blocker: string | null;
  demo_override_allowed?: boolean;
  demo_override?: { decision: 'accepted' | 'denied' | 'flagged' | null; original_queue: string } | null;
}
export interface ReviewCatalog {
  items: ReviewCatalogItem[];
  counts: { total: number; claimable: number; phone: number; blocked: number };
  truncated?: boolean;
}

export interface Flag {
  code: string;
  severity: string;
  detail: string | null;
  blocks_review: boolean;
  suppresses_settlement: boolean;
}

export interface MediaPart {
  index: number;
  url: string;
  bytes: number;
  file: string;
}

export interface Episode {
  episode_id: string;
  session_folder: string;
  ingest_id: string;
  task: { id: string; name: string; price_per_minute: Decimal; currency: string } | null;
  collector: { id: string; display_name: string } | null;
  scenario: { code: string; privacy_risk_level: string } | null;
  declared: {
    others_in_frame: boolean;
    sensitive_info_present: boolean;
    session_origin: string;
  } | null;
  device: { serial: string | null; firmware: string | null };
  measured_duration_seconds: Decimal;
  claimed_duration_seconds: Decimal | null;
  timing: { source: string | null; confidence: string | null };
  frame_rate: number | null;
  recorded_at: string | null;
  flags: Flag[];
  resolver_note: {
    state: string;
    method: string | null;
    confirmed: boolean;
    reason: unknown;
    start_source: unknown;
    start_confidence: unknown;
    candidate_count: unknown;
  };
  media: { role: string | null; parts: MediaPart[] };
}

export interface Claim extends Episode {
  review_id: string;
  lease_expires_at: string | null;
  queue_depth: number;
  session_average_seconds: number | null;
}

/**
 * All three labels are non-null since migration 0018.
 *
 * `label_vi` used to be `string | null` and `label_zh` used to be `string`,
 * which was the wrong way round both times: the columns were equally nullable
 * and the reviewer-facing one was the one the compiler was told to trust. The
 * database now refuses a reason code that is missing either, so this type says
 * what the server can actually send.
 */
export interface ReasonCode {
  code: string;
  category: string;
  label_en: string;
  label_zh: string;
  label_vi: string;
}

export interface RecentReview {
  reviewId: string;
  episodeId: string;
  reviewState: string;
  measured: Decimal;
  effective: Decimal | null;
  reviewedAt: string | null;
  seconds: number | null;
  amount: Decimal | null;
}

/**
 * The shift counters behind Home's gauge and its ledger.
 *
 * This lived in `Home.tsx` next to a hand-rolled `fetch`, and that is what lost
 * the reference on a 500: the route built its own `ApiError` from the status
 * line alone, so `error.ref` was always `undefined` and `<Problem>` had nothing
 * to quote. The type is here now and the request goes through `call`, which
 * reads the body every other request on this seam already reads.
 */
export interface Shift {
  currency: string;
  reviewer: string;
  target: number;
  decided: number;
  approved: number;
  payable_seconds: Decimal;
  median_seconds_to_verdict: Decimal | null;
  settled_amount: Decimal;
  queue_depth: number;
  session_average_seconds: number | null;
  needs_human: number;
}

export interface VerdictResult {
  episode_id: string;
  decision: Verdict;
  effective_duration_seconds: Decimal | null;
  settlement?: {
    unit_price?: Decimal;
    effective_minutes?: Decimal;
    amount?: Decimal;
  } | null;
  replayed?: boolean;
}

export interface VerdictRequest {
  verdict_id: string;
  episode_id: string;
  decision: Verdict;
  spans: { start_seconds: number; end_seconds: number }[];
  reject_reasons: string[];
  reviewer_note?: string | null;
  time_to_verdict_seconds?: number | null;
}

/**
 * A failed request, carrying the status so callers can act on the ones that
 * mean something specific.
 *
 * 409 is the interesting case, and it is NOT one thing. It is either "somebody
 * else holds this episode now", which the server says by answering
 * `{"error":"reassigned"}`, or a refusal that names itself in `constraint` —
 * the session has no task claim, the disputed settlement was billed, the
 * measured duration is implausible. The two need opposite answers on screen:
 * the first is a lost lease and the reviewer takes the next episode, the second
 * is a sentence about THIS episode and an action on it.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
    /**
     * The refusal this response named, when it named one. Separate from
     * `detail`, which a `reassigned` 409 fills with an English sentence that
     * is not a refusal name and must not be looked up as one.
     */
    readonly constraint?: string,
    /**
     * The parsed error body, when the response had one. A 500 that carries a
     * report — `payout_batch_aborted` is the only one today — is still an
     * error, and the report inside it is still the record of what was sent.
     */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * The server's own id for this failure, when it gave one.
   *
   * A 500 body is deliberately a constant — it must not echo the statement or
   * its parameters — so `{"error":"internal"}` was all an operator could see
   * and all they could report. The server now logs the failure against
   * Fastify's request id and returns that same id as `ref`, so the sentence on
   * screen and the line in the log can be joined by somebody reading them
   * hours apart. It is a generated id and says nothing about the request,
   * which is why it is safe to put in front of a user.
   */
  get ref(): string | undefined {
    const r = (this.body as { ref?: unknown } | undefined)?.ref;
    return typeof r === 'string' ? r : undefined;
  }

  /** The session is gone. The router sends these back to sign-in. */
  get isUnauthenticated() {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Somebody else holds this episode now.
   *
   * Read off the body, not off the status code. It used to be `status === 409`
   * alone, and that made every named refusal on the verdict path look like an
   * expired lease: a reviewer who tripped `session_claim_missing` was told the
   * claim had gone and to take the next episode, the translated sentence for
   * what actually happened never rendered, and the episode came back to the
   * next reviewer to be refused again.
   */
  get isReassigned() {
    return this.status === 409 && this.message === 'reassigned';
  }

  /**
   * The server refused for a policy reason, not a technical one: remote
   * playback of raw footage is not authorised (brief D11, Part 7.3). A state to
   * explain, never a request to retry.
   */
  get isWithheld() {
    return this.status === 451;
  }
}

let ending = false;
export const sessionIsEnding = () => ending;
export function sessionEnded() {
  if (ending || ['/login', '/discover', '/privacy'].includes(window.location.pathname.replace(/\/$/, ''))) return;
  ending = true;
  window.location.replace('/login?ended=1&next=' + encodeURIComponent(window.location.pathname + window.location.search));
}

export async function call<T>(path: string, init?: RequestInit): Promise<T | null> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  /** 204 is "the queue is empty", which is a state and not an absence of one. */
  if (res.status === 204) return null;

  if (!res.ok) {
    let message = res.statusText;
    let detail: unknown;
    let constraint: string | undefined;
    let body: unknown;
    try {
      body = await res.json();
      const parsed = body as { error?: string; detail?: unknown; constraint?: string };
      if (parsed.error) message = parsed.error;
      /**
       * A back-office 409 carries the constraint that refused it rather than a
       * sentence, so the console can say why in the reader's own language
       * instead of echoing an English string the server chose.
       */
      detail = parsed.detail ?? parsed.constraint;
      constraint = parsed.constraint;
    } catch {
      /* A non-JSON error body is still an error; the status carries it. */
    }
    throw new ApiError(res.status, message, detail, constraint, body);
  }

  return (await res.json()) as T;
}

/* -------------------------------------------------------------------------
   The back office (BO-01 to BO-04). Same transport, same cookies.
   ---------------------------------------------------------------------- */

export interface BoTask {
  id: string;
  name: string;
  type: string | null;
  /** A decimal string. Displayed as written; the server multiplies it. */
  unit_price: Decimal;
  target_effective_duration_s: Decimal | null;
  max_concurrent_claimants: number;
  status: 'draft' | 'published' | 'taken_down';
  claimants: number;
}

export interface BoAgreement {
  agreement: string;
  version: string;
  accepted_at: string;
}

/** The masked view of a collector's current payout account. `null` is the state that matters. */
export interface BoPayoutAccount {
  method: 'WALLET' | 'BANK_ACCOUNT' | 'BANK_CARD';
  verify_status: string;
  phone_masked: string;
}

/**
 * The console's copy of `collectors_status_check`, in the order the lifecycle
 * runs: `prospect` (migration 0034 — signed up in the app, never been to a
 * centre), then the three an operator writes.
 *
 * One list, because it was three: two screens render
 * `bo.collector.status.<status>` from live data, the back office's row select
 * built its options separately, and the type said something different again.
 * 0034 added a value and all three disagreed with the database at once — a
 * counter operator read the literal key `bo.collector.status.prospect`.
 * `collector-status.test.tsx` holds this against the catalogue and the row.
 *
 * It is what can be SHOWN and lifted, not what can be typed: the create form
 * keeps its own three, and `CollectorBody`/`CollectorPatch` in the API refuse
 * `prospect` outright. An operator lifts a prospect; nobody makes one.
 */
export const BO_COLLECTOR_STATUSES = ['prospect', 'pending', 'qualified', 'suspended'] as const;

export type BoCollectorStatus = (typeof BO_COLLECTOR_STATUSES)[number];

export interface BoCollector {
  id: string;
  external_ref: string;
  status: BoCollectorStatus;
  exam_result: 'pass' | 'fail' | null;
  exam_current_passed?: boolean;
  agreements_current?: boolean;
  exam_decided_at: string | null;
  agreements: BoAgreement[];
  /** `null` means nobody has declared one, which is why this collector cannot be paid. */
  payout_account: BoPayoutAccount | null;
}

/** What the counter declares on a collector's behalf. The full number is sent and not stored. */
export interface BoPayoutDeclaration {
  id: string;
  method: BoPayoutAccount['method'];
  declared_name: string;
  phone?: string;
  bank_code?: string;
  account_no?: string;
}

export interface BoPayoutDeclared {
  id: string;
  replayed: boolean;
  verify_status: string;
  declared_name: string;
  verified_name: string | null;
  account_no_last4: string | null;
  phone_masked: string;
  onboarding_url: string | null;
  reform_url: string | null;
}

export interface BoDevice {
  id: string;
  hardware_serial: string;
  firmware_version: string | null;
  status: 'active' | 'faulty' | 'retired';
  fault_note: string | null;
  bound_collector_id: string | null;
  bound_collector_ref: string | null;
  bound_at: string | null;
  device_type_code: string | null;
}

const send = (path: string, body?: unknown) =>
  call<unknown>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

const patch = (path: string, body: unknown) =>
  call<unknown>(path, { method: 'PATCH', body: JSON.stringify(body) });

/** What BO-01 lets an operator change about a task after it exists. */
export interface BoTaskEdit {
  name?: string;
  type?: string;
  unit_price?: string;
  target_effective_duration_s?: string | null;
  max_concurrent_claimants?: number;
  status?: BoTask['status'];
}

export const backOffice = {
  tasks: () => call<{ tasks: BoTask[] }>('/api/tasks'),
  createTask: (body: {
    id: string;
    name: string;
    type: string;
    unit_price: string;
    target_effective_duration_s?: string;
    max_concurrent_claimants: number;
  }) => send('/api/tasks', body),
  setTask: (id: string, body: BoTaskEdit) => patch(`/api/tasks/${id}`, body),
  setTaskStatus: (id: string, status: BoTask['status']) => patch(`/api/tasks/${id}`, { status }),

  collectors: () =>
    call<{ required_agreements: string[]; collectors: BoCollector[] }>('/api/collectors'),
  createCollector: (body: {
    id: string;
    external_ref: string;
    status?: BoCollector['status'];
  }) => send('/api/collectors', body),
  setCollector: (
    id: string,
    body: {
      status?: BoCollector['status'];
      /** `null` clears a result recorded against the wrong person. */
      exam?: { result: 'pass' | 'fail'; decided_at: string } | null;
      agreements?: BoAgreement[];
    },
  ) => patch(`/api/collectors/${id}`, body),
  /**
   * The counter's payout declaration. On the payout lane by URL, but it is a
   * counter route — any operator session, scoped to their own centre — and the
   * back office is the only screen that calls it, so its client lives here
   * beside the rest of that screen rather than in `payout`, which is finance's.
   */
  declarePayoutAccount: (collectorId: string, body: BoPayoutDeclaration) =>
    call<BoPayoutDeclared>(`/api/payout/collectors/${collectorId}/accounts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  devices: () =>
    call<{ devices: BoDevice[]; device_types: { id: string; code: string }[] }>('/api/devices'),
  createDevice: (body: {
    id: string;
    device_type_id: string;
    hardware_serial: string;
    firmware_version?: string;
  }) => send('/api/devices', body),
  setDevice: (
    id: string,
    body: {
      status?: BoDevice['status'];
      firmware_version?: string | null;
      fault_note?: string | null;
    },
  ) => patch(`/api/devices/${id}`, body),
  bindDevice: (id: string, collectorId: string) =>
    send(`/api/devices/${id}/bind`, { collector_id: collectorId }),
  unbindDevice: (id: string) => send(`/api/devices/${id}/unbind`),

  /**
   * APP-10, taken by an operator on the collector's behalf.
   *
   * The id is minted by the caller and the write is idempotent on it, like
   * every other create on this seam. There is deliberately no `claims()` read
   * beside this: the API has no operator-reachable list of claims — `GET
   * /api/me/claims` is scoped to a collector's own token — so the console can
   * create a claim and cannot enumerate one. `GET /api/tasks` carries the live
   * claimant *count*, which is what the table shows.
   */
  claimTask: (taskId: string, body: { id: string; collector_id: string }) =>
    send(`/api/tasks/${taskId}/claims`, body),

  /**
   * Who holds this camera from now on: a custody period, not a bind.
   *
   * `bindDevice` is "who has it in their hands"; this is what settlement reads
   * to answer "who had it on 13 August". One POST does both halves of a swap,
   * so the caller never closes a period itself.
   */
  assignDevice: (deviceId: string, body: { id: string; collector_id: string; valid_from: string }) =>
    send(`/api/devices/${deviceId}/assignments`, body),
};

/* -------------------------------------------------------------------------
   The counter (BO-10). Two writes and one read, and the two writes are
   deliberately separate objects: the handover is the card arriving, the
   session is what was recorded on it. `CLAUDE.md` calls that split a decision
   — the app binds a session before recording (APP-16), the operator creates
   the handover when the card arrives — and in the pilot the operator creates
   both, which is why every session written from here is stamped
   `session_origin = 'handover'` by the server.
   ---------------------------------------------------------------------- */

/**
 * The offline cache's reference data, as `GET /reference/sync` sends it.
 *
 * Rows straight off the tables, so the keys are the schema's camelCase and not
 * the snake_case the hand-written routes use. Its scope is the other thing
 * worth knowing: **authorisation** is centre-scoped and the payload is not, so
 * a collector who claimed from one task hall can hand a card over at whichever
 * centre they reach. ADR 0003 carries that argument; `reference_scope` says it
 * on the wire.
 */
export interface Reference {
  fetched_at: string;
  upload_centre_id: string;
  reference_scope: 'global';
  collectors: {
    id: string;
    externalRef: string;
    /** `Counter.tsx` and `TaskAssign.tsx` render this straight through `t()`. */
    status: BoCollectorStatus;
    examResult: 'pass' | 'fail' | null;
    examCurrentPassed?: boolean;
    agreementsCurrent?: boolean;
  }[];
  devices: {
    id: string;
    hardwareSerial: string;
    status: 'active' | 'faulty' | 'retired';
    firmwareVersion: string | null;
    boundCollectorId: string | null;
  }[];
  tasks: {
    id: string;
    name: string;
    type: string | null;
    unitPrice: Decimal;
    status: 'draft' | 'published' | 'taken_down';
  }[];
  scenarios: { id: string; code: string; privacyRiskLevel: 'low' | 'medium' | 'high' }[];
  /** Live and released claims both; `releasedAt === null` is the live one. */
  task_claims: {
    id: string;
    taskId: string;
    collectorId: string;
    claimedAt: string;
    releasedAt: string | null;
  }[];
}

/** What `POST /handovers` takes. Centre, operator and machine come from the tokens. */
export interface HandoverBody {
  id: string;
  collector_id: string;
  device_id: string;
  tf_card_id: string;
  handover_time: string;
}

/**
 * What `POST /handovers/:id/sessions` takes.
 *
 * `others_in_frame` and `sensitive_info_present` are booleans with no default,
 * on purpose and all the way down: the column is NOT NULL, the server's schema
 * is `z.boolean()` rather than `.default(false)`, and the wizard makes it a
 * required choice. "Nobody asked" is not one of the answers.
 *
 * There is **no `session_ended_at`**, here or anywhere. An operator cannot
 * supply a truthful end, and a retroactively typed end that decides payment
 * attribution is the failure the brief warns about.
 */
export interface SessionBody {
  id: string;
  task_id: string;
  scenario_id: string;
  collection_point_id?: string;
  others_in_frame: boolean;
  sensitive_info_present: boolean;
  prepare_time: string;
  client_version?: string;
}

export const counter = {
  reference: () => call<Reference>('/reference/sync'),
  handover: (body: HandoverBody) => send('/handovers', body),
  session: (handoverId: string, body: SessionBody) =>
    send(`/handovers/${handoverId}/sessions`, body),
};

export const api = {
  /** Claims the next episode, or null when there is nothing to review. */
  claimNext: (queue: ReviewQueue = 'standard', episodeId?: string) => call<Claim>(`/api/review/claim?queue=${queue}`, {
    method: 'POST', ...(episodeId ? { body: JSON.stringify({ episode_id: episodeId }) } : {}),
  }),

  reviewCatalog: (queue: ReviewQueue) => call<ReviewCatalog>(`/api/review/catalog?queue=${queue}`),
  demoReview: (id: string, action: DemoReviewAction, reason?: string) => call<{ demo_only: true; queue: ReviewQueue; decision: 'accepted' | 'denied' | 'flagged' | null; original_queue: string }>(`/api/review/demo/${encodeURIComponent(id)}`, {
    method: 'POST', body: JSON.stringify({ action, ...(reason ? { reason } : {}) }),
  }),

  /** Metadata without claiming — this is what warms the next video element. */
  episode: (id: string) => call<Episode>(`/api/review/episode/${id}`),

  /** What is next in the queue, without taking it. */
  peek: (queue: ReviewQueue = 'standard') => call<Episode>(`/api/review/next?queue=${queue}`),

  reasons: () => call<{ reasons: ReasonCode[] }>('/api/review/reasons'),

  /** The shift counters on Home. */
  shift: () => call<Shift>('/api/review/shift'),

  recent: () => call<{ currency: string; reviews: RecentReview[] }>('/api/review/recent'),

  heartbeat: (id: string) => call<unknown>(`/api/review/heartbeat/${id}`, { method: 'POST' }),

  release: (id: string) => call<unknown>(`/api/review/release/${id}`, { method: 'POST' }),

  /**
   * Parks an episode the server refused a verdict on (0017).
   *
   * `held` is not a lane a claim may ask for, so the row stops being served the
   * moment this returns. The reason is mandatory at the server: an episode that
   * leaves every claimable lane and comes back only when a person puts it back
   * has to carry, in words, what the counter is being asked to fix.
   */
  hold: (episodeId: string, reason: string) =>
    call<{ episode_id: string; review_id: string; queue: string }>(
      `/api/review/hold/${episodeId}`,
      { method: 'POST', body: JSON.stringify({ queue: 'held', reason }) },
    ),

  verdict: (body: VerdictRequest) =>
    call<VerdictResult>('/api/review/verdict', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  whoami: () => call<{ actor?: unknown }>('/whoami'),

  /**
   * Releasing a lease when the tab closes.
   *
   * `sendBeacon` is the only thing that reliably runs on unload, and it cannot
   * set headers — which is exactly why the session also travels as cookies.
   * Without this a reviewer who closes the tab locks an episode out of the
   * queue until the lease expires ten minutes later.
   */
  releaseOnUnload: (id: string) => {
    navigator.sendBeacon(`/api/review/release/${id}`);
  },
};

/* -------------------------------------------------------------------------
   Settlement and payout (SET-03 → SET-07, and the payout brief). Same
   transport, same cookies. Every figure below is a server figure rendered as
   received: the console never adds, multiplies or rounds any of them.
   ---------------------------------------------------------------------- */

/** `bills.total` as stored: numeric(14,4) text. */
export interface SettleBill {
  id: string;
  collector_ref: string;
  period_start: string;
  period_end: string;
  currency: string;
  total: Decimal;
  generated_at: string;
  lines: number;
  paid: boolean;
  exceptions: number;
}

export interface GenerateRollover {
  settlements: number;
  collector_refs: string[];
}

export interface GenerateResult {
  period_start: string;
  period_end: string;
  cycle_days: number;
  created: number;
  not_payable: number;
  deferred_to_next_period: GenerateRollover;
  skipped: GenerateRollover;
  /** Parked settlements in the window, on a bill or not. */
  exception: number;
  bills: SettleBill[];
}

export interface SettleLine {
  settlement_id: string;
  episode_id: string;
  ingest_id: string;
  review_id: string;
  task: string;
  unit_price: Decimal;
  effective_minutes: Decimal;
  amount: Decimal;
  settlement_state: string;
  reviewed_at: string | null;
}

export interface SettleBillDetail {
  id: string;
  collector_ref: string;
  period_start: string;
  period_end: string;
  currency: string;
  total: Decimal;
  generated_at: string;
  paid: boolean;
  exceptions: number;
  lines: SettleLine[];
}

export type OriginalityDecision = 'cleared' | 'reused' | 'accepted_unassessable';
export interface OriginalityState {
  ingest_id: string;
  payable: boolean;
  disposition: OriginalityDecision | 'pending';
  identity_verified: boolean;
  assessments: { id: string; status: string; decision: OriginalityDecision | null; reason: string | null;
    evidence: { reason?: string; media_verified?: boolean; matching_ingests?: { ingest_id: string; method: string;
      segments?: { qStart: number; qEnd: number; cStart: number; cEnd: number; scale?: number; reversed?: boolean; mirrored?: boolean }[] }[] } }[];
}
export const originality = {
  ingest: (id: string) => call<OriginalityState>(`/api/originality/ingests/${encodeURIComponent(id)}`),
  decide: (id: string, decision: OriginalityDecision, reason: string) =>
    call<{ id: string }>(`/api/originality/assessments/${encodeURIComponent(id)}/decision`, {
      method: 'POST', body: JSON.stringify({ decision, reason }),
    }),
};

export type PayoutMethod = 'WALLET' | 'BANK_ACCOUNT' | 'BANK_CARD';
export type VerifyStatus =
  | 'unverified'
  | 'verified'
  | 'name_mismatch'
  | 'no_wallet'
  | 'locked'
  | 'kyc_limit'
  | 'error';
export type AttemptStatus =
  | 'created'
  | 'submitted'
  | 'processing'
  | 'pending_zlp'
  | 'succeeded'
  | 'failed'
  | 'unknown';
export type PayoutMode = 'manual' | 'api';

/** What stands between a bill and a transfer, as `worker/batch.ts` lists it. */
export type PayoutIssue =
  | 'no_account'
  | 'account_unverified'
  | 'over_bank_ceiling'
  | 'under_bank_minimum'
  | 'under_one_dong'
  | 'over_cap'
  | 'risk_hold'
  | 'originality_pending'
  | 'attempt_open'
  | 'already_paid'
  | 'line_in_exception';

export type RiskBand = 'clear' | 'notice' | 'review' | 'hold';
export type RiskSeverity = 'info' | 'notice' | 'review' | 'hold';

/** The risk seam, §2.3 of the payout brief, exactly as the batch route emits it. */
export interface RiskFlag {
  signalId: string;
  severity: RiskSeverity;
  points: number;
  /** The numbers the sentence is built from. Rendered as a table under it, always. */
  evidence: Record<string, unknown>;
  thresholdVersion: string;
  computedAt: string;
  /**
   * Present only when the summary came through the risk engine's own routes,
   * which render the sentence server-side in every locale. The batch route
   * does not carry it; the console renders from the same templates then.
   */
  sentence?: Partial<Record<string, string>>;
}

export interface RiskSummary {
  subjectType: 'collector' | 'episode' | 'bill' | 'batch';
  subjectId: string;
  score: number;
  band: RiskBand;
  flags: RiskFlag[];
}

export interface PayoutAccountSummary {
  simulation?: boolean;
  id: string;
  method: PayoutMethod;
  verify_status: VerifyStatus;
  declared_name: string;
  verified_name: string | null;
  phone_masked: string;
}

export interface PayoutAttempt {
  id: string;
  seq: number;
  partner_order_id: string;
  mode: PayoutMode;
  status: AttemptStatus;
  zlp_order_id: string | null;
  zp_trans_id: string | null;
  sub_return_code: number | null;
  manual_reference: string | null;
  poll_count: number;
  last_polled_at: string | null;
  created_at: string;
  settled_at: string | null;
}

/** One bill of a payout batch, `shapeBill` in `payout/routes/payout.ts`. */
export interface PayoutBill {
  simulation?: boolean;
  id: string;
  collector_id: string;
  collector_ref: string;
  period_start: string;
  period_end: string;
  currency: string;
  total: Decimal;
  /** What a transfer moves: the total rounded down to whole dong. */
  amount_vnd: number;
  lines: number;
  paid: boolean;
  account: PayoutAccountSummary | null;
  attempt: PayoutAttempt | null;
  risk: RiskSummary;
  issues: PayoutIssue[];
}

export interface PayoutBatch {
  period_start: string;
  period_end: string;
  mode: PayoutMode;
  /** The start day of the most recent period that holds a bill, or null when none does. */
  latest_bill_period: string | null;
  bills: PayoutBill[];
}

/** The preflight, `Preflight` in `payout/worker/batch.ts`, plus the bills it names. */
export interface PayoutPreflight {
  period_start: string;
  period_end: string;
  mode: PayoutMode;
  bills: number;
  payable: number;
  total_vnd: number;
  required_vnd: number;
  balance_vnd: number | null;
  shortfall_vnd: number;
  ok: boolean;
  /** The server's own sentence, in English. Shown as what the server said, beside the translated summary. */
  refusal: string | null;
  counts: Record<PayoutIssue, number>;
  risk_bands: Record<RiskBand, number>;
  cap_vnd: number | null;
  bank_ceiling_vnd: number;
  exceptions: PayoutBill[];
}

/** One period of a collector's income, server-computed, `/collectors/:id/income`. */
export interface IncomePeriod {
  bill_id: string | null;
  period_start: string;
  period_end: string;
  valid_minutes: Decimal;
  gross: Decimal;
  withheld: Decimal;
  net: Decimal;
  status: 'pending_review' | 'approved' | 'paid' | 'on_hold';
}

export interface PayoutEvent {
  kind: string;
  evidence: Record<string, unknown>;
  occurred_at: string;
}

export interface PayoutAttemptDetail {
  id: string;
  billId: string;
  partnerOrderId: string;
  attemptSeq: number;
  amountVnd: number;
  mode: PayoutMode;
  status: AttemptStatus;
  zlpOrderId: string | null;
  zpTransId: string | null;
  subReturnCode: number | null;
  manualReference: string | null;
  lastPolledAt: string | null;
  pollCount: number;
  createdAt: string;
  settledAt: string | null;
  events: PayoutEvent[];
}

export interface PayResult {
  bill_id: string;
  attempt_id: string;
  partner_order_id: string;
  status: AttemptStatus;
  zlp_order_id?: string | null;
  sub_return_code?: number | null;
  amount_vnd?: number;
  manual_reference?: string;
  settled_at?: string | null;
}

/**
 * What one server-side batch run reports: `BatchRun` in
 * `payout/worker/batch.ts`. The batch is a loop on the server — preflight at
 * entry, one transfer at a time with a pause between, stop at the first
 * refusal, a ticket when the whole batch is refused. The console never
 * iterates `/pay` itself; it asks for the run and renders this.
 */
export interface BatchRun {
  period_start: string;
  period_end: string;
  mode: PayoutMode;
  preflight: Omit<PayoutPreflight, 'exceptions' | 'mode'>;
  sent: { bill_id: string; attempt_id: string; partner_order_id: string; status: AttemptStatus; result: string }[];
  /** Every bill the run did not send, with the constraint name the catalogue maps to a sentence. */
  refused: { bill_id: string; collector_ref: string; constraint: string }[];
  /** The bill id the run stopped at, or null. Its constraint is in `refused`. */
  stopped_at: string | null;
  tickets: { kind: string; bill_id: string | null; evidence: unknown; occurred_at: string }[];
}

/**
 * Whether this session may pay.
 *
 * The identity route answers `operator` for every counter session and does
 * not carry the finance role, and `index.ts` is not this console's to change.
 * Until it does, the question is put to a finance-gated route in a form it
 * refuses before touching anything: an id that is not a UUID. The role check
 * is a pre-handler, so somebody without the role is turned away with 403
 * before the handler runs, and somebody with it reaches the handler and is
 * told 400 for the bad id. Neither path reads a bill, writes a row or audits
 * anything. The screens treat `unknown` as read-only.
 */
/**
 * `administrator` is a reader here, not an actor: the demo runs on one
 * administrator credential and it has to be able to see what it is debugging.
 * `canReadFinance` in payout/role.ts is the read question; `readOnlyReason`
 * is still the action question, and it disables the controls for this role.
 */
export type FinanceRole = 'finance' | 'administrator' | 'operator' | 'unknown';

const batchPath = (start: string) => `/api/payout/batches/${encodeURIComponent(start)}`;

export const settle = {
  bills: (periodStart: string) =>
    call<{ period_start: string; period_end: string; bills: SettleBill[] }>(
      `/api/settle/bills?period_start=${encodeURIComponent(periodStart)}`,
    ),
  /** SET-07: bill every pending settlement of the period. Idempotent on the server. */
  generate: (periodStart: string) =>
    call<GenerateResult>('/api/settle/bills', {
      method: 'POST',
      body: JSON.stringify({ period_start: periodStart }),
    }),
  bill: (id: string) => call<SettleBillDetail>(`/api/settle/bills/${id}`),
  /** The per-line CSV (SET-06). A plain link: the cookies go with it. */
  linesCsvUrl: (periodStart: string) =>
    `/api/settle/export.csv?period_start=${encodeURIComponent(periodStart)}`,
};

export const payout = {
  batch: (periodStart: string) => call<PayoutBatch>(batchPath(periodStart)),
  preflight: (periodStart: string) =>
    call<PayoutPreflight>(`${batchPath(periodStart)}/preflight`, { method: 'POST' }),
  income: (collectorId: string) =>
    call<{ collector_id: string; currency: string; periods: IncomePeriod[] }>(
      `/api/payout/collectors/${collectorId}/income`,
    ),
  attempt: (id: string) => call<PayoutAttemptDetail>(`/api/payout/attempts/${id}`),
  /** The API rail, one bill. Refused with `payout_mode_manual` in the pilot's default mode. */
  pay: (billId: string) => call<PayResult>(`/api/payout/bills/${billId}/pay`, { method: 'POST' }),
  /**
   * The API rail, the whole period, as ONE server-side run of `runBatch`.
   *
   * Served by `POST /api/payout/batches/:period/run` on feat/payout-domain
   * (finance, audited, one run per period at a time — a second caller is
   * refused 409 `payout_batch_running`). A server without it answers 404 and
   * the screen says so.
   */
  runBatch: async (
    periodStart: string,
  ): Promise<(BatchRun & { aborted: boolean }) | null> => {
    try {
      const run = await call<BatchRun>(`${batchPath(periodStart)}/run`, { method: 'POST' });
      if (run === null) return null;
      return { ...run, aborted: false };
    } catch (err) {
      /**
       * A run may throw after earlier transfers committed. Only the named
       * aborted response is turned back into a report, and only when every
       * report collection is present; every other error keeps its old path.
       */
      const body =
        err instanceof ApiError && err.status === 500
          ? (err.body as Partial<BatchRun> & { error?: string } | undefined)
          : undefined;
      if (
        body?.error === 'payout_batch_aborted' &&
        Array.isArray(body.sent) &&
        Array.isArray(body.refused) &&
        Array.isArray(body.tickets) &&
        body.preflight !== undefined
      ) {
        return { ...(body as BatchRun), aborted: true };
      }
      throw err;
    }
  },
  /**
   * The manual rail. The reference is the transfer the operator made; the
   * amount is retyped by the operator and checked by the database against
   * the bill, never computed here.
   */
  markPaid: (billId: string, body: { manual_reference: string; amount_vnd: number }) =>
    call<PayResult>(`/api/payout/bills/${billId}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resolve: (
    attemptId: string,
    body: { outcome: 'succeeded' | 'failed'; reason: string; zp_trans_id?: string },
  ) =>
    call<{ attempt_id: string; status: AttemptStatus; zp_trans_id: string | null; settled_at: string | null }>(
      `/api/payout/attempts/${attemptId}/resolve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** The hashed payout CSV (finance). A plain link, so the browser downloads it with the cookies. */
  exportUrl: (periodStart: string) => `/api/payout/export/${encodeURIComponent(periodStart)}`,

  financeRole: async (): Promise<FinanceRole> => {
    const profile = await call<{ operator?: { role?: string; status?: string } }>('/api/operator/profile');
    if (!profile?.operator?.role || profile.operator.status !== 'active') return 'unknown';
    return profile.operator.role === 'finance' || profile.operator.role === 'administrator' ? profile.operator.role : 'operator';
  },
};

/* -------------------------------------------------------------------------
   The risk engine's own routes (Agent C). The summary on a payout bill comes
   from the batch route above; these add the hold trail and the one action an
   operator takes — clearing a hold with a typed reason. A server without the
   engine answers 404 here, and the screen says so rather than hiding the
   controls.
   ---------------------------------------------------------------------- */

export type ClearVerdict = 'false_positive' | 'accepted' | 'resolved';

export interface RiskHold {
  hold_id: string;
  raised_by_flag: string;
  raised_at: string;
  signal_ids: string[];
  cleared_at: string | null;
  cleared_by: string | null;
  clear_reason: string | null;
  clear_verdict: ClearVerdict | null;
}

/** A flag as the engine's routes shape it: snake_case, with the sentence in every locale. */
interface ShapedFlag {
  signal_id: string;
  severity: RiskSeverity;
  points: number;
  threshold_version: string;
  computed_at: string;
  evidence: Record<string, unknown>;
  sentence: Record<string, string>;
}

interface ShapedSummary {
  subject_type: RiskSummary['subjectType'];
  subject_id: string;
  score: number;
  band: RiskBand;
  evaluated_at: string | null;
  flags: ShapedFlag[];
}

/** One shape in the console, whichever route the summary came from. */
const camelSummary = (s: ShapedSummary): RiskSummary & { evaluatedAt: string | null } => ({
  subjectType: s.subject_type,
  subjectId: s.subject_id,
  score: s.score,
  band: s.band,
  evaluatedAt: s.evaluated_at,
  flags: s.flags.map((f) => ({
    signalId: f.signal_id,
    severity: f.severity,
    points: f.points,
    evidence: f.evidence,
    thresholdVersion: f.threshold_version,
    computedAt: f.computed_at,
    sentence: f.sentence,
  })),
});

export const risk = {
  billSummary: async (billId: string) => {
    const s = await call<ShapedSummary>(`/api/risk/summary/bill/${billId}`);
    return s === null ? null : camelSummary(s);
  },
  holds: (billId: string) =>
    call<{ bill_id: string; held: boolean; history: RiskHold[] }>(`/api/risk/holds/${billId}`),
  clearHold: (billId: string, body: { reason: string; verdict: ClearVerdict }) =>
    call<{ bill_id: string; cleared_hold: string; held: boolean }>(`/api/risk/holds/${billId}/clear`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

/* -------------------------------------------------------------------------
   The counter's episode lane (`packages/api/src/episodes.ts`).

   These four routes are mounted at the API root rather than under `/api`, and
   they do not share one scope — which is the whole reason the screen that
   reads them has to say which scope each list is in:

   - `/upload-batches` and `/upload-batches/:id/exceptions` are **machine**
     scoped, through `actor.machine.uploadDeviceId`. The list window is the
     server's own default of 100, capped at 500, and its time filters read
     `import_started_at` — import time, never recording time.
   - `/episodes/stuck` is **upload-centre** scoped, through
     `actor.operator.uploadCentreId`, and has no window at all.
   - `/api/episodes/:id/outcome` is scoped by the episode id it is given.

   Every field below is a field the server already sends; nothing here is
   derived, and nothing is inferred from a batch's candidate sessions.
   ---------------------------------------------------------------------- */

/** One row of `GET /upload-batches`. The two counts are per resolution state. */
export interface BatchRow {
  id: string;
  handoverId: string;
  batchStatus: 'importing' | 'imported' | 'uploading' | 'verifying' | 'verified' | 'closed' | 'failed';
  importStartedAt: string;
  importCompletedAt: string | null;
  resolved: number;
  quarantined: number;
}

/**
 * `GET /upload-batches/:id/exceptions`.
 *
 * `blocking` is what holds the batch open; `summary` counts the same batch
 * including the parked episodes, which block nothing (0018) and are still
 * worth an operator's eye. `episodes_per_session` is null when the delivery
 * declared no session, and is the one figure to read even when nothing is
 * wrong.
 *
 * `unusable` is the OTHER population, and it is not a subset of `blocking`.
 * These episodes have a session — so nothing holds the batch open on their
 * account — and the recording behind them is one the engine could not read, so
 * `eligible` in review.ts will never hand it to a reviewer. Attributed and
 * unpayable, which is why it needs its own list rather than a count.
 */
export interface BatchExceptions {
  batch_id: string;
  summary: {
    episodes: number;
    sessions: number;
    quarantined: number;
    awaiting_confirmation: number;
    parked: number;
    unusable: number;
    episodes_per_session: number | null;
  };
  blocking: {
    episode_id: string;
    /**
     * `YYYYMMDD_HHMMSS` from the recording directory's basename, not an ISO
     * instant — `episodes.session_started_at` is a text column (schema.ts:109).
     * `Episodes.tsx`'s `stamp()` is what reads it; `new Date()` cannot.
     */
    session_started_at: string | null;
    resolution_state: string;
    needs: 'assignment' | 'confirmation';
  }[];
  unusable: {
    episode_id: string;
    /** The same basename stamp as above; `stamp()` reads it, `new Date()` cannot. */
    session_started_at: string | null;
    resolution_state: string;
    ingest_state: string | null;
    /**
     * The defect codes from `episode_defects`, in the order the server stored
     * them. Shown verbatim: `MEDIA-UNREADABLE` is the evidence an operator
     * repeats down the phone, and translating it would make it unquotable.
     */
    defects: string[];
  }[];
  sessions: { id: string; prepareTime: string; sessionOrigin: string; collectorId: string }[];
}

/** `GET /episodes/stuck`. An episode can carry both holds at once. */
export interface StuckEpisode {
  episode_id: string;
  device_serial: string;
  /** The basename stamp again, `YYYYMMDD_HHMMSS`. Never parsed as a date. */
  session_started_at: string;
  resolution_state: string;
  park: {
    park_id: string;
    reason: string | null;
    parked_at: string;
    parked_by: string | null;
    release_with: string;
  } | null;
  held: { review_id: string; held_at: string; release_with: string } | null;
}

/** `GET /api/episodes/:id/outcome`. All three labels are non-null since 0018. */
export interface EpisodeOutcome {
  episode_id: string;
  collector_id: string | null;
  ingest_id: string | null;
  review_state: string | null;
  reviewed_at: string | null;
  reviewer_note: string | null;
  reasons: { code: string; category: string; label_en: string; label_vi: string; label_zh: string }[];
}

export const episodes = {
  /** The machine's own batches, newest first, in the server's default window. */
  batches: () => call<{ batches: BatchRow[] }>('/upload-batches?limit=500'),
  exceptions: (batchId: string) => call<BatchExceptions>(`/upload-batches/${batchId}/exceptions`),
  stuck: () => call<{ episodes: StuckEpisode[] }>('/episodes/stuck'),
  outcome: (episodeId: string) => call<EpisodeOutcome>(`/api/episodes/${episodeId}/outcome`),
  /**
   * PLT-05's human resolution path. The reason is mandatory at the database,
   * so the form makes it mandatory too rather than letting the write fail.
   */
  resolve: (episodeId: string, body: { collection_session_id: string; reason: string }) =>
    call<{ episode_id: string; resolution_state: string; resolution_method: string }>(
      `/episodes/${episodeId}/resolve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};
